import { DropdownSelect, SelectItem, Tooltip } from '@dixa/lib-shared-habla';
import { useHasAccess } from 'lib-shared-access-control';
import { ampli } from 'lib-shared-tracking';
import intersectionWith from 'lodash/intersectionWith';
import { useMemo } from 'react';

import {
  getCSATLanguages,
  SupportedCSATLanguageCode,
} from 'app/views/layout/views/settings/views/satisfaction/utils/supportedCSATLanguages/supportedCSATLanguages';
import { satisfactionActions } from 'components/conversation/actions/satisfaction';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { bindActionToPromise } from 'utils/sagaApiGeneratorHelpers';

const defaultLanguage = 'en';

type ConversationHeaderCsatDropdownProps = {
  /** Will disable the dropdown and show this text in the button when present. */
  disabledText?: string;
  /** Loading state for the dropdown */
  isLoading: boolean;
  /** Whether the user is excluded from CSAT */
  isUserExcluded: boolean;
  /** Available languages from the API */
  availableLanguages: readonly string[];
};

const userIsExcludedTooltipMessage = 'This user is excluded';

export const ConversationHeaderCsatDropdown = ({
  disabledText,
  isLoading,
  isUserExcluded,
  availableLanguages,
}: ConversationHeaderCsatDropdownProps): JSX.Element => {
  const notify = useNotifications();
  const dispatch = useAppDispatch();
  const supportedLanguages = useMemo(() => getCSATLanguages(), []);
  const hasCsatCancelationPermission = useHasAccess('settings.satisfaction');
  const hasCsatChangeLanguagePermission = useHasAccess(
    'conversation.satisfaction',
  );

  const [{ id: conversationId, attributes }] = useAppSelector((state) => [
    state.conversation.metaConversation.conversation,
  ]);

  const languageCodeItems = useMemo(() => {
    const matchingLanguages = intersectionWith(
      supportedLanguages,
      availableLanguages,
      (a, b) => a.code === b,
    );
    return matchingLanguages.map(({ code, label }) => ({
      id: code,
      name: label,
      description: code,
    }));
  }, [supportedLanguages, availableLanguages]);

  const selectedLanguage: SupportedCSATLanguageCode | undefined =
    useMemo(() => {
      if (
        attributes?.language &&
        availableLanguages.includes(attributes.language)
      ) {
        return attributes.language as SupportedCSATLanguageCode;
      }
      if (availableLanguages.includes(defaultLanguage)) {
        return defaultLanguage;
      }
      return availableLanguages[0] as SupportedCSATLanguageCode;
    }, [availableLanguages, attributes?.language]);

  const handleLanguageSelect = ({ id: languageCode }) => {
    if (!hasCsatChangeLanguagePermission) {
      notify(
        {
          title: 'Failed to change CSAT language',
          text: 'You do not have permission to change the CSAT language',
        },
        NotificationType.Error,
      );

      return;
    }

    ampli.conversationCsatMenuLanguageSelected({
      languageCode,
    });
    bindActionToPromise(
      dispatch,
      satisfactionActions.changeSatisfactionLanguageRequest,
    )({ csid: conversationId, language: languageCode });
  };

  const handleCancelCsat = () => {
    ampli.conversationCsatMenuCancelOptionClicked();
    bindActionToPromise(
      dispatch,
      satisfactionActions.cancelSatisfactionRequest,
    )({ csid: conversationId });
  };

  return (
    <Tooltip
      disabled={!!disabledText}
      content={`CSAT - Customer Satisfaction ${
        isUserExcluded ? `(${userIsExcludedTooltipMessage})` : ''
      }`}
    >
      <DropdownSelect
        buttonProps={{
          icon: 'smile',
          disabled: !!disabledText || isUserExcluded,
          isLoading: isLoading,
          variant: 'tertiary',
        }}
        items={languageCodeItems}
        itemComponent={SelectItem}
        selectedItems={selectedLanguage ? [selectedLanguage] : []}
        onSelect={handleLanguageSelect}
        onClear={hasCsatCancelationPermission ? handleCancelCsat : undefined}
        onClearText="Cancel CSAT"
        onToggle={(isOpen) => {
          if (isOpen) {
            ampli.conversationCsatMenuOpened();
          }
        }}
      >
        {disabledText || selectedLanguage}
      </DropdownSelect>
    </Tooltip>
  );
};
